// @flow

import find from 'lodash/find';

import type {
  AppState,
  Screen,
  Toast,
  InitializeSuccessAction,
  SetAppStateAction,
  NavigateAction,
  SignOutAction,
  AddToastAction,
  RemoveToastAction,
  ShowWarningBarAction,
  HideWarningBarAction
} from '../common/typings';
import { SIGN_OUT } from '../authentication/actions';
import { ADD_TOAST, REMOVE_TOAST } from '../utils/actions';
import { APP_STATE_INITIALIZING, APP_STATE_CHOOSE_SHIFT } from '../constants';

import {
  INITIALIZE_SUCCESS,
  SET_APP_STATE,
  NAVIGATE,
  SHOW_WARNING_BAR,
  HIDE_WARNING_BAR
} from './actions';

export type State = {|
  +isInitialized: boolean,
  +guid: ?string,
  +kioskSessionId: number | null,
  +appState: AppState,
  +screen: ?Screen,
  +screenProps: {},
  +toasts: Array<Toast>,
  +warningBar: {
    +isVisible: boolean,
    +message: string
  }
|};

type Action =
  | InitializeSuccessAction
  | SetAppStateAction
  | NavigateAction
  | SignOutAction
  | AddToastAction
  | RemoveToastAction
  | ShowWarningBarAction
  | HideWarningBarAction;

export const initialState = {
  isInitialized: false,
  guid: null,
  kioskSessionId: null,
  appState: APP_STATE_INITIALIZING,
  screen: null,
  screenProps: {},
  toasts: [],
  warningBar: {
    isVisible: false,
    message: ''
  }
};

export default function core(state: State = initialState, action: Action) {
  switch (action.type) {
    case INITIALIZE_SUCCESS:
      return {
        ...state,
        isInitialized: true,
        guid: action.payload.guid,
        kioskSessionId: action.payload.kioskSessionId,
        appState: APP_STATE_CHOOSE_SHIFT
      };
    case SET_APP_STATE:
      return {
        ...state,
        appState: action.payload.appState
      };
    case NAVIGATE: {
      const screenProps = action.payload.screenProps || {};
      return {
        ...state,
        screen: action.payload.screen,
        screenProps: action.payload.screen ? screenProps : {}
      };
    }
    case SIGN_OUT:
      return {
        ...state,
        appState: APP_STATE_CHOOSE_SHIFT,
        screen: null,
        screenProps: {}
      };
    case ADD_TOAST: {
      const { message } = action.payload;
      const toastExists = find(state.toasts, ({ text }) => text === message);

      if (toastExists) {
        return state;
      }

      const id = `${state.toasts.length}-${Date.now()}`;
      const toast = {
        id,
        text: action.payload.message,
        type: action.payload.type,
        displayTime: action.payload.displayTime,
        includeTimer: action.payload.includeTimer
      };
      return {
        ...state,
        toasts: [...state.toasts, toast]
      };
    }
    case REMOVE_TOAST: {
      const { id } = action.payload;
      return {
        ...state,
        toasts: (state.toasts.filter(toast => toast.id !== id): Array<Toast>)
      };
    }
    case SHOW_WARNING_BAR:
      return {
        ...state,
        warningBar: {
          message: action.payload.message,
          isVisible: true
        }
      };
    case HIDE_WARNING_BAR:
      return {
        ...state,
        warningBar: {
          message: '',
          isVisible: false
        }
      };
    default:
      // (action: empty); is a way to enforce exhaustiveness in flow.
      // That means every action provided in Action type have to be handled in reducer.
      (action: empty); // eslint-disable-line
      return state;
  }
}
